import React from 'react';

import { Steps } from 'antd';
import CollapsibleComponent from 'components/CollapsibleContainer';
import CollectionRedirect from 'components/CollectionRedirect/CollectionRedirect';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import PageContainer from 'components/Containers/PageContainer';
import { Row } from 'components/Containers/Row/Row';
import SectionContainer from 'components/Containers/SectionContainer';
import { SmallModal } from 'components/Modals/SmallModal';
import WhatsNext from 'components/WhatsNext/WhatsNext';
import Label from 'components/form/Label/Label';
import { Link } from 'components/navigation/Link/Link';
import { ConditionalRendering } from 'helpers/conditionalRedering.helper';
import { onClickCheckAll3BureauCreditReport } from 'hook/negativeMarks.hook';
import StaticOfferContainer from 'partial/OfferManager/StaticOfferContainer';
import ListNegativeMarksAccounts from 'partial/OfferManager/modules/GoalCreditRepairExpert/components/ListNegativeMarksAccounts/ListNegativeMarksAccounts';
import NegativeMarksAccountsItem from 'partial/OfferManager/modules/GoalCreditRepairExpert/components/NegativeMarksAccountsItem/NegativeMarksAccountsItem';
import { frequentlyQuestion } from 'partial/OfferManager/modules/GoalCreditRepairExpert/constants';
import CurrentPotentialPaymentGrade from './components/CurrentPotentialPaymentGrade/CurrentPotentialPaymentGrade';
import PaymentHistoryCreditScoreMessage from './components/PaymentHistoryCreditScoreMessage/PaymentHistoryCreditScoreMessage';
import { isFree } from 'helpers/userHelper';

const { Step } = Steps;

const StepIcon = () => (
  <div className="width18px height18px border-rad50 bgLgreen200 margl6" />
);
const StepTitle = ({ title }) => (
  <Label type="Subtitle-1" className="inline">
    <strong>{title}</strong>
  </Label>
);
const StepDescription = ({ description }) => (
  <Label className="Body-1">{description}</Label>
);

const stepStatus = 'finish';

const GoalNegativeMarksView = props => {
  const { showDownloadAppComponent } = props?.config?.appManagement;
  const delinquentAccounts = props?.delinquentAccounts ?? [];
  const derogatoryAccounts = props?.derogatoryAccounts ?? [];
  const onClickCreditReport = () => {
    onClickCheckAll3BureauCreditReport(props?.eventHandler, props?.module);
  };
  const isFreeUser = isFree(props?.userInfo);
  const linkTo = isFreeUser
    ? props?.subscriptionLandingUrl
    : '/creditReports/view';

  return (
    <PageContainer className="GoalCreditRepairExpertView">
      <SectionContainer>
        <FlexContainer>
          <div className="format90 m-format100">
            <Row>
              <Label
                type="H3"
                typeHeader={'h3'}
                datatestid={`${props?.defaultTestId}-title`}
              >
                Raise your payment history grade
              </Label>
            </Row>
            <Row className="m-margb0">
              <Label type="Body-1">
                You have {props?.totalNegativeMarks} negative{' '}
                {props?.totalNegativeMarks > 1 ? 'marks' : 'mark'} on your
                payment history. Negative marks can remain on your credit report
                for 7-10 years. Try to reduce your negative marks to raise your
                payment history grade.
              </Label>
            </Row>
            <Row className="m-block desktopHide">
              <CurrentPotentialPaymentGrade
                currentPaymentHistoryGrade={props?.paymentHistoryGrade}
                potentialPaymentHistoryGrade={
                  props?.potentialPaymentHistoryGrade
                }
              />
            </Row>
            <Row className="margb0">
              <ListNegativeMarksAccounts
                {...props}
                totalNegativeMarks={props?.totalNegativeMarks}
                accounts={delinquentAccounts.concat(derogatoryAccounts)}
              />
            </Row>
            <Row className="m-block desktopHide">
              <PaymentHistoryCreditScoreMessage
                showHideModal={props?.showHideModal}
              />
            </Row>
          </div>
          <div className="mPoof padt50">
            <Row className="padlr30">
              <CurrentPotentialPaymentGrade
                currentPaymentHistoryGrade={props?.paymentHistoryGrade}
                potentialPaymentHistoryGrade={
                  props?.potentialPaymentHistoryGrade
                }
              />
              <PaymentHistoryCreditScoreMessage
                showHideModal={props?.showHideModal}
              />
            </Row>
          </div>
        </FlexContainer>
      </SectionContainer>

      <SectionContainer>
        <Row>
          <Label className="margb20" type="H3" typeHeader={'h3'}>
            Actions you can take
          </Label>
          <Label type="Body-1">
            Pay off outstanding debts, make payments on time, focus on building
            positive credit history and seek professional help.
          </Label>
        </Row>
        <Row>
          <Steps className="action-plan-steps" direction="vertical" current={0}>
            <Step
              icon={<StepIcon />}
              status={stepStatus}
              title={
                <StepTitle title="Reduce your negative marks with the help of a credit repair expert." />
              }
              description={
                <>
                  <StepDescription description="Negative marks can be a thing of the past, and working with Lexington Law can finally help you address them." />
                  <div className="padlr4 margt16">
                    <StaticOfferContainer
                      modulePosition={props?.modulePosition}
                      eventHandler={props?.eventHandler}
                      module={props?.module}
                      pagePosition={props?.pagePosition}
                      offerMetadataList={props?.offers}
                      extraAttributes={props?.simulationAttributes}
                    />
                  </div>
                </>
              }
            />
            <Step
              icon={<StepIcon />}
              status={stepStatus}
              title={<StepTitle title="Check your 3-bureau credit report" />}
              description={
                <StepDescription
                  description={
                    <>
                      <Link onClickCallback={onClickCreditReport} to={linkTo}>
                        Check your credit report{' '}
                      </Link>{' '}
                      to make sure all your negative marks are accurate.
                    </>
                  }
                />
              }
            />

            {props?.actionPlanSteps.map(item => (
              <Step
                key={item.title.toLowerCase().split('').join('-')}
                icon={<StepIcon />}
                status={stepStatus}
                title={<StepTitle title={item.title} />}
                description={
                  <StepDescription
                    description={
                      <>
                        <NegativeMarksAccountsItem
                          className="format50 m-format100"
                          accounts={item.accounts}
                          removeBorder
                        />
                        {item.message}
                        {showDownloadAppComponent && (
                          <ConditionalRendering
                            isTrue={item?.showCollectionBanner}
                          >
                            <CollectionRedirect
                              className="margt16 border-rad10px format80 m-format100"
                              moduleName={props?.module}
                              eventHandler={props?.eventHandler}
                            />
                          </ConditionalRendering>
                        )}
                      </>
                    }
                  />
                }
              />
            ))}
            <Step
              className="poof-hard"
              icon={null}
              status={null}
              title={null}
              description={null}
            />
          </Steps>
        </Row>
      </SectionContainer>
      <WhatsNext
        sectionTitle="What to expect"
        leftTitle="If you ignore negative marks"
        leftContent="This can have severe consequences for your credit score and
                  ability to obtain credit in future. It may lead to legal
                  action, wage garnishment, bank account levies or lawsuits."
        rightTitle="If you want to resolve negative marks"
        rightContent="Take action as soon as possible. Check your credit report,
                  dispute errors, communicate with creditors, make payment on
                  time and get professional help. The longer you wait the harder
                  it is to repair your credit score."
      />

      <SectionContainer>
        <Row className="bgWhite">
          <Label type="H4" typeHeader={'h4'} className="margb32">
            Frequently Asked Questions
          </Label>
          {frequentlyQuestion.map(item => (
            <>
              <div className="margT16 bgWhite"> </div>
              <CollapsibleComponent
                className="flex-1 bgGray0"
                isExpanded={false}
                contentTitle={
                  <Label className={'H5 mSubtitle-1'} tabIndex="-1">
                    {item?.question}
                  </Label>
                }
              >
                <div className={'padL48 padR16 padB24 bgGray0'}>
                  <Label className={'Body-2 mBody-2'}>{item?.answer}</Label>
                </div>
              </CollapsibleComponent>
            </>
          ))}
        </Row>
      </SectionContainer>
      <SmallModal
        footer=""
        onCancel={props?.showHideModal}
        visible={props?.showHowItIsCalculated}
        closable
      >
        <Label className="margb20 bold m-padlr32" type="h3" typeHeader={'h3'}>
          How is this calculated?
        </Label>
        <Label className="margb40 m-padlr32" type="Body-1">
          Your payment history grade is based on your past behavior with
          payments. This includes the number of on-time, late and missed
          payments, whether there any accounts have been sent to collections or
          charged off and the number bankruptcies, foreclosures or other major
          negative items on your report.
        </Label>
      </SmallModal>
    </PageContainer>
  );
};

export default GoalNegativeMarksView;
